import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '../../../utils/apolloClient';
import { environmentURLs } from '../../../components/common/CaptureEnvironment';
import { API_TYPES } from '../../../constants/APIDirectoryConstants';
import { BUNDLE_TYPE, RESOURCE_TYPES } from '../../../constants/constants';

export const normalizePlanNames = (items) => {
  if (!Array.isArray(items)) return items;
  
  return items.map((item) => ({
    ...item,
    pricingPlans: Array.isArray(item.pricingPlans)
      ? item.pricingPlans.map((plan) => ({
          ...plan,
          name: plan.plan_name ?? plan.name,
        }))
      : item.pricingPlans,
  }));
};

const initialState = {
  cart: [],
  obsoleteApisInCart: [],
  fetchingCartDetails: false,
  errorFetchingCartDetails: false,
  requestedAPIsToRemoveFromCart :[],
  deletingApi: false,
  errorDeletingApi: false,
  selectedFieldsMapping: {},
  webhookURLMapping: {},
  selectedApiPricing: {},
  termsAndConditionsSelected: false,
  addingApiToCart: false,
  errorAddingApiToCart: false,
  errorCreatingOrder: false,
  errorUpdatingOrder: false,
  creatingOrder: false,
  orderDetails: [],
  orderCreated: false,
  addingPricingPlanToApiInCart: false,
  errorAddingPricingPlanToApiInCart: false,
  successAddingPricingPlanToApiInCart: false,
  orders: {
    app: {},
    restApis: [],
    asyncApis: [],
  },
  getOrderInProgress: false,
  getOrderError: false,
  fetchingOrderDetails: false,
  updatingOrder: false,
  orderUpdated: false,
  webhookUpdated: false,

  generatingSecret: false,
  errorInGeneratingSecret: false,
  secretGenerated: false,
  lastFetched: null,
  consents: {},
  bundleConsents: {},
  fromPurchaseFlow:false,
  apiDeleted: false,
  deletedApiName: '',
  activeApiList: [],
  deletingBundle: false,
  errorDeletingBundle: false,
  bundleDeleted: false,
  deletedBundleName: '',
  requestingRestrictedAccess: false,
  errorRequestingRestrictedAccess: false,
  restrictedAccessRequested: false,
  requestingApiId: null,
  isBundleSelectedLocationsPending: false,
  isBundleSelectedLocationsErrored: false,
  bundleSelectedLocationsError: [],
  bundleSelectedLocations: [],
  isAddBundleLocationsPending: false,
  isAddBundleLocationsSuccess: false,
  isAddBundleLocationsErrored: false,
  addBundleLocationsError: null,
  addBundleLocationsResponse: null,
  isRemoveBundleLocationsPending: false,
  isRemoveBundleLocationsSuccess: false,
  isRemoveBundleLocationsErrored: false,
  removeBundleLocationsError: null,
  removeBundleLocationsResponse: null,
  fetchingStampedTermsDocuments: false,
  errorFetchingStampedTermsDocuments: false,
  stampedTermsDocuments: [],
  stampedTermsDocumentsCallCount: 0,
  savingBundleConsents: false,
  errorSavingBundleConsents: false,
  bundleConsentLinks: [],
  subscriptionCounts: {},
  fetchingSubscriptionCounts: false,
  errorFetchingSubscriptionCounts: false,
};

const removeBundleFromOrderState = (state, bundleId) => {
  const bundleIntegrations = Array.isArray(state.orders?.bundleIntegrations)
    ? state.orders.bundleIntegrations
    : [];
  const bundleIntegration = bundleIntegrations.find(
    (bundle) => bundle.id === bundleId
  );
  const remainingBundleIntegrations = bundleIntegrations.filter(
    (bundle) => bundle.id !== bundleId
  );

  const apiKeysInOtherBundles = new Set();
  remainingBundleIntegrations.forEach((bundle) => {
    (bundle.apis || []).forEach((api) => {
      if (!api?.id) return;
      apiKeysInOtherBundles.add(`${api.id}::${api.apiType || ''}`);
    });
  });

  const removableApiKeys = new Set(
    (bundleIntegration?.apis || [])
      .filter((api) => api?.id)
      .map((api) => `${api.id}::${api.apiType || ''}`)
      .filter((apiKey) => !apiKeysInOtherBundles.has(apiKey))
  );

  state.orders.restApis = (state.orders.restApis || []).filter(
    (api) => !removableApiKeys.has(`${api.id}::${api.type || ''}`)
  );
  state.orders.asyncApis = (state.orders.asyncApis || []).filter(
    (api) => !removableApiKeys.has(`${api.id}::${api.type || ''}`)
  );
  state.orders.bundleIntegrations = remainingBundleIntegrations;

  state.bundleConsents = Object.fromEntries(
    Object.entries(state.bundleConsents).filter(([key]) => key !== bundleId)
  );
};

export const addAPIToCart = createAsyncThunk(
  'addAPIToCart',
  async ({ payload, token }) => {
    let response = await client.authorizeRequest({
      method: 'POST',
      url: `${environmentURLs.REACT_APP_DEALER_API_BUYER_SERVICE_URL}/cart`,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      data: { ...payload },
    });
    return { data: response.data };
  }
);
//update
export const updateAPIPricingPlanId = createAsyncThunk(
  'addPricingPlanToAPIInCart',
  async ({ payload, token }) => {
    let response = await client.authorizeRequest({
      method: 'PATCH',
      url: `${environmentURLs.REACT_APP_DEALER_API_BUYER_SERVICE_URL}/cart`,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      data: { ...payload },
    });
    return { data: response.data, pricingPlanDetails: { ...payload } };
  }
);

export const createOrder = createAsyncThunk(
  'createOrder',
  async ({ payload, token }) => {
    let response = await client.authorizeRequest({
      method: 'POST',
      url: `${environmentURLs.REACT_APP_DEALER_API_BUYER_SERVICE_URL}/order`,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      data: { ...payload },
    });
     //delay to allow backend to stabilize before proceeding
    return { data: response.data };
  }
);
export const updateOrder = createAsyncThunk(
  'updateOrder',
  async ({ payload, token, appId, updateWebhook, pricingPlanUpdated, webhookApiId, newWebhookUrl }) => {
    let response = await client.authorizeRequest({
      method: 'PATCH',
      url: `${environmentURLs.REACT_APP_DEALER_API_BUYER_SERVICE_URL}/order/${appId}`,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      data: { ...payload },
    });
    //delay to allow backend to stabilize before proceeding
    await new Promise((resolve) => setTimeout(resolve, 3000));
    return { data: response.data, updateWebhook, pricingPlanUpdated, webhookApiId, newWebhookUrl };
  }
);

export const getCartDetails = createAsyncThunk(
  'getCartDetails',
  async ({ entityId, token, isExpanded = false }) => {
    let response = await client.authorizeRequest({
      method: 'GET',
      url: `${environmentURLs.REACT_APP_DEALER_API_BUYER_SERVICE_URL}/cart?organizationId=${entityId}&isExpanded=${isExpanded}`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return { data: response.data };
  }
);

export const deleteApiDetails = createAsyncThunk(
  'deleteApiDetails',
  async ({ entityId, token, resourceIds }) => {
    let response = await client.authorizeRequest({
      method: 'delete',
      url: `${environmentURLs.REACT_APP_DEALER_API_BUYER_SERVICE_URL}/cart?organizationId=${entityId}&resourceIds=${resourceIds}`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  }
);

export const deleteBundle = createAsyncThunk(
  'deleteBundle',
  async ({ orderId, bundleId, token }) => {
    const response = await client.authorizeRequest({
      method: 'POST',
      url: `${environmentURLs.REACT_APP_DEALER_API_BUYER_SERVICE_URL}/order/${orderId}/bundles/${bundleId}/delete`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return { ...response, bundleId };
  }
);

export const deleteApi = createAsyncThunk(
  'deleteApi',
  async ({ orderId, apiId, token, apiType, apiOrgId, appName }) => {
    let response = await client.authorizeRequest({
      method: 'POST',
      url: `${environmentURLs.REACT_APP_DEALER_API_BUYER_SERVICE_URL}/order/${orderId}/apis/${apiId}/delete`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: {
        api: {
          type: apiType,
          orgId: apiOrgId,
        },
        appName,
      },
    });
    return response;
  }
);

export const getOrders = createAsyncThunk(
  'getOrders',
  async ({ token }) => {
    const response = await client.authorizeRequest({
      method: 'GET',
      url: `${environmentURLs.REACT_APP_DEALER_API_BUYER_SERVICE_URL}/order?isExpanded=true`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return { data: response.data };
  }
);

export const getSubscriptionCounts = createAsyncThunk(
  'getSubscriptionCounts',
  async ({ token, appId }) => {
    const response = await client.authorizeRequest({
      method: 'GET',
      url: `${environmentURLs.REACT_APP_DEALER_API_BUYER_SERVICE_URL}/order/apis/subscription-counts?appId=${encodeURIComponent(appId)}`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return { data: response.data };
  }
);

export const getOrderAPIConsents = createAsyncThunk(
  'getOrderAPIConsents',
  async ({appId, token} ) => {
    const response = await client.authorizeRequest({
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`
      },
      url: `${environmentURLs.REACT_APP_MP_DP_METADATA_URL}/solutions/${appId}/consents?consentType=api_tos`
    });
    return {
      ...response.data
    };
  }
);

export const getStampedTermsDocuments = createAsyncThunk(
  'getStampedTermsDocuments',
  async ({
    orgName,
    orgId,
    appName,
    bundleNames,
    consentingDealerUser,
    firstName,
    lastName,
    userId,
    email,
    token,
  }) => {
    const date = new Date().toISOString();
    const response = await client.authorizeRequest({
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      url: `${environmentURLs.REACT_APP_DEALER_API_BUYER_SERVICE_URL}/order/stamp-document`,
      data: {
        orgName,
        orgId,
        appName,
        bundleNames,
        consentingDealerUser,
        firstName,
        lastName,
        userId,
        email,
        date,
      },
    });
    return response.data;
  },
);

export const saveBundleConsents = createAsyncThunk(
  'saveBundleConsents',
  async ({ orgId, bundles, token }) => {
    const documents = bundles.map(({ bundleId, pdfDocument }) => ({ bundleId, pdfDocument }));
    const response = await client.authorizeRequest({
      method: 'POST',
      url: `${environmentURLs.REACT_APP_DEALER_API_BUYER_SERVICE_URL}/order/consent-document`,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      data: { orgId, documents },
    });
    return { data: response.data, bundles };
  },
);

export const generateNewSecret = createAsyncThunk(
  'generateNewSecret',
  async ({ token,appId,}) => {
    const response = await client.authorizeRequest({
      method: 'POST',
      url: `${environmentURLs.REACT_APP_DEALER_API_BUYER_SERVICE_URL}/order/${appId}/keys`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return { data: response.data };
  }
);

export const submitRestrictedAccessRequest = createAsyncThunk(
  'submitRestrictedAccessRequest',
  async ({ apiId, apiType, token }) => {
    const isRestAPI = API_TYPES.REST === apiType;
    const baseEndpoint = `${
      isRestAPI
        ? environmentURLs.REACT_APP_API_GATEWAY
        : environmentURLs.REACT_APP_ASYNC_API_SERVICE
    }/v1/${isRestAPI ? 'api' : 'async-api'}`;
    let response = await client.authorizeRequest({
      method: 'POST',
      url: `${baseEndpoint}/${apiId}/request-access`,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return { data: response.data };
  }
);

export const bundleSelectedLocations = createAsyncThunk(
  'bundleSelectedLocations',
  async ({ entityId, orderId, token }) => {
    let response = await client.authorizeRequest({
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`
      },
      url: `${environmentURLs.REACT_APP_DEALER_API_BUYER_SERVICE_URL}/orders/${orderId}/bundle-locations`
    });
    return {
      response: {
        data: response?.data
      },
      entityId,
    };
  }
);

export const addBundleLocations = createAsyncThunk(
  'addBundleLocations',
  async ({ locations, token, bundleId, orderId, bundleDisplayName }) => {
    const bundleLocationsArray = locations?.map(location => ({
      bundleId: bundleId,
      orgId: location?.entityId,
      cmf: location?.cmfNumber
    }));
    
    const payload = {
      bundleLocations: bundleLocationsArray,
      bundleDisplayName: bundleDisplayName,
    };
   
    let response = await client.authorizeRequest({
      method: 'POST',
      data: payload,
      headers: {
        Authorization: `Bearer ${token}`
      },
      url: `${environmentURLs.REACT_APP_DEALER_API_BUYER_SERVICE_URL}/orders/${orderId}/bundle-locations`
    });
    return {
      response: {
        data: response?.data
      },
      locations,
      bundleId,
      orderId
    };
  }
);

export const removeBundleLocations = createAsyncThunk(
  'removeBundleLocations',
  async ({ orgIds, bundleId, apis, token, orderId, bundleDisplayName }) => {
    const data = { orgIds, bundleId, apis, bundleDisplayName };
    let response = await client.authorizeRequest({
      method: 'PATCH',
      data,
      headers: {
        Authorization: `Bearer ${token}`
      },
      url: `${environmentURLs.REACT_APP_DEALER_API_BUYER_SERVICE_URL}/orders/${orderId}/bundle-locations`,
      params: { status: 'retired' },
    });
    return {
      response: {
        data: response?.data
      },
      orgIds,
      bundleId,
      apis,
      orderId
    };
  }
);

export const orderAPISlice = createSlice({
  name: 'orderAPISlice',
  initialState,
  reducers: {
    updateSelectedFieldsMapping: (state, action) => {
      state.selectedFieldsMapping = action.payload;
    },
    updateWebhookURLMapping: (state, action) => {
      state.webhookURLMapping = action.payload;
    },
    updateSelectedPriceMapping: (state, action) => {
      const { selectedAPI, planId } = action.payload;
      const pricingMapping = { ...state.selectedApiPricing };
      pricingMapping[selectedAPI] = planId;
      state.selectedApiPricing = { ...pricingMapping };
    },
    updateTermsAndConditionsMapping: (state) => {
      state.termsAndConditionsSelected = !state?.termsAndConditionsSelected;
    },
    updateCart: (state, action) => {
      state.cart = [...action.payload.cart];
      state.obsoleteApisInCart = action.payload.obsoleteApis;
    },
    resetAddingPricingPlanToApiInCart: (state, action) => {
      state.errorAddingPricingPlanToApiInCart = false;
      state.successAddingPricingPlanToApiInCart = false;
    },
    resetErrorCreatingOrder: (state) => {
      state.errorCreatingOrder = false;
    },
    resetErrorUpdatingOrder: (state) => {
      state.errorUpdatingOrder = false;
    },
    clearToastNotification: (state) => {
      state.orderUpdated = false;
      state.webhookUpdated = false;
      state.errorUpdatingOrder = false;
      state.apiDeleted = false;
      state.errorDeletingApi = false;
      state.deletedApiName = '';
      state.bundleDeleted = false;
      state.deletedBundleName = '';
    },

    storeDeletedApiName: (state, action) => {
      state.deletedApiName = action.payload.apiName;
    },
     storeDeletedBundleName: (state, action) => {
      state.deletedBundleName = action.payload.bundleName;
    },
    storeActiveApiList: (state, action) => {
      state.activeApiList = action.payload;
    },
    resetState: () => initialState,
    setfromPurchaseFlowFlag: (state) => {
      state.fromPurchaseFlow = true;
    },
    resetCreateandUpdateOrderFlags: (state,action) =>{
      state.orderCreated = false;
      state.orderUpdated = false;
      state.webhookUpdated = false;
    },
    clearAddBundleLocationsState: (state) => {
      state.isAddBundleLocationsPending = false;
      state.isAddBundleLocationsSuccess = false;
      state.isAddBundleLocationsErrored = false;
      state.addBundleLocationsError = null;
      state.addBundleLocationsResponse = null;
    },
    clearRemoveBundleLocationsState: (state) => {
      state.isRemoveBundleLocationsPending = false;
      state.isRemoveBundleLocationsSuccess = false;
      state.isRemoveBundleLocationsErrored = false;
      state.removeBundleLocationsError = null;
      state.removeBundleLocationsResponse = null;
    },
    updateBundleCMFSelectionInCart: (state, action) => {
      const { bundleId, selectedCMFNumber, selectedStoreId } = action.payload;
      state.cart = state.cart.map(item =>
        item.resourceType?.toLowerCase() === RESOURCE_TYPES.BUNDLE && item.resourceId === bundleId
          ? {
              ...item,
              selectedCMFNumber: selectedCMFNumber,
              selectedStoreId: selectedStoreId,
            }
          : item
      );
    }
  },
  extraReducers: {
    [getCartDetails.pending]: (state) => {
      state.fetchingCartDetails = true;
      state.errorFetchingCartDetails = false;
      state.cart = [];
      state.obsoleteApisInCart = [];
      state.selectedFieldsMapping = {};
      state.webhookURLMapping = {};
      state.selectedApiPricing = {};
      state.termsAndConditionsSelected = false;
    },
    [getCartDetails.fulfilled]: (state, { payload }) => {
      state.fetchingCartDetails = false;
      state.errorFetchingCartDetails = false;
      state.cart = payload.data;
    },
    [getCartDetails.rejected]: (state) => {
      state.fetchingCartDetails = false;
      state.errorFetchingCartDetails = true;
    },
    [deleteApiDetails.pending]: (state, action) => {
      state.requestedAPIsToRemoveFromCart = [
        ...state.requestedAPIsToRemoveFromCart,
        action?.meta?.arg?.resourceIds,
      ];
      state.errorDeletingApi = false;
    },
    [deleteApiDetails.fulfilled]: (state, action) => {
      state.cart = state.cart.filter(
        (item) => item.resourceId !== action?.meta?.arg?.resourceIds
      );
      state.requestedAPIsToRemoveFromCart =
        state.requestedAPIsToRemoveFromCart?.filter(
          (item) => item !== action?.meta?.arg?.resourceIds
        );
    },
    [deleteApiDetails.rejected]: (state, action) => {
      state.requestedAPIsToRemoveFromCart =
        state.requestedAPIsToRemoveFromCart?.filter(
          (item) => item !== action?.meta?.arg?.resourceIds
        );
      state.errorDeletingApi = true;
    },
    [addAPIToCart.pending]: (state) => {
      state.addingApiToCart = true;
      state.errorAddingApiToCart = false;
    },
    [addAPIToCart.fulfilled]: (state, { payload, meta }) => {
      state.addingApiToCart = false;
      const requestPayload = meta.arg.payload;
      if (requestPayload.bundleId) {
        if (requestPayload.bundleType === BUNDLE_TYPE.PRICE) {
        const bundleItems = payload.data.map((item) => ({
          ...item,
          resourceId: requestPayload.bundleId,
          bundleType: requestPayload.bundleType,
          resourcePricingPlanId: requestPayload.bundlePricingPlanId,
          resourceType: RESOURCE_TYPES.BUNDLE,
        }));
        state.cart = [...state.cart, ...bundleItems];
        }else{
         state.cart = [...state.cart, ...payload.data];
        }
       
      } else if (payload.data) {
        state.cart = [...state.cart, payload.data?.[0]];
      }
    },
    [addAPIToCart.rejected]: (state) => {
      state.addingApiToCart = false;
      state.errorAddingApiToCart = true;
    },
    [updateAPIPricingPlanId.pending]: (state) => {
      state.addingPricingPlanToApiInCart = true;
      state.errorAddingPricingPlanToApiInCart = false;
      state.successAddingPricingPlanToApiInCart = false;
    },
    [updateAPIPricingPlanId.fulfilled]: (state, { payload }) => {
      state.addingPricingPlanToApiInCart = false;
      state.cart = state.cart.map((item) =>
        item.resourceId === payload?.pricingPlanDetails?.resourceId
          ? {
              ...item,
              resourcePricingPlanId: payload.pricingPlanDetails.resourcePricingPlanId,
            }
          : item
      );
      state.errorAddingPricingPlanToApiInCart = false;
      state.successAddingPricingPlanToApiInCart = true;
    },
    [updateAPIPricingPlanId.rejected]: (state) => {
      state.addingPricingPlanToApiInCart = false;
      state.errorAddingPricingPlanToApiInCart = true;
      state.successAddingPricingPlanToApiInCart = false;
    },
    [createOrder.pending]: (state) => {
      state.creatingOrder = true;
      state.errorCreatingOrder = false;
    },
    [createOrder.fulfilled]: (state, { payload }) => {
      state.creatingOrder = false;
      state.orderCreated = true;
    },
    [createOrder.rejected]: (state) => {
      state.creatingOrder = false;
      state.errorCreatingOrder = true;
    },
    [updateOrder.pending]: (state) => {
      state.updatingOrder = true;
      state.errorUpdatingOrder = false;
    },
    [updateOrder.fulfilled]: (state, { payload }) => {
      state.updatingOrder = false;
      if (payload.updateWebhook) {
        state.webhookUpdated = true;
        if (payload.webhookApiId && payload.newWebhookUrl !== undefined) {
          state.orders.asyncApis = (state.orders.asyncApis || []).map((api) => {
            if (api.id !== payload.webhookApiId) return api;
            const integrations = Array.isArray(api.integrations)
              ? [...api.integrations]
              : [];
            if (integrations.length > 0) {
              integrations[0] = { ...integrations[0], webhookUrl: payload.newWebhookUrl };
            } else {
              integrations.push({ webhookUrl: payload.newWebhookUrl });
            }
            return { ...api, integrations };
          });
        }
      } else if (payload.pricingPlanUpdated) {
        state.orderUpdated = true;
        state.pricingPlanUpdated = { ...payload.pricingPlanUpdated };
      } else {
        state.orderUpdated = true;
      }
    },
    [updateOrder.rejected]: (state) => {
      state.updatingOrder = false;
      state.errorUpdatingOrder = true;
    },
    [getOrders.pending]: (state) => {
      state.isProcessing = true;
      state.errorInProcess = false;
      state.fetchingOrderDetails = true;
    },
    [getOrders.fulfilled]: (state, { payload }) => {
      state.isProcessing = false;
      state.lastFetched = Date.now();
      state.orders = {
        app: payload.data?.app || {},
        restApis: normalizePlanNames(payload.data?.restApis || []),
        asyncApis: normalizePlanNames(payload.data?.asyncApis || []),
        bundleIntegrations: normalizePlanNames(payload.data?.bundleIntegrations || []),
      };
      payload.data?.bundleIntegrations?.forEach((bundleIntegration) => {
        state.bundleConsents[bundleIntegration.id] = {
          signedTermsAndConditions:
            bundleIntegration.consent.signedTermsAndConditions,
          updatedDate: bundleIntegration.consent.updatedDate,
          name: bundleIntegration.consent.consentName,
          userEmail: bundleIntegration.consent.userEmail,
        };
      });
     state.fetchingOrderDetails = false;
    },
    [getOrders.rejected]: (state) => {
      state.isProcessing = false;
      state.errorInProcess = true;
      state.fetchingOrderDetails = false;
    },
    [getSubscriptionCounts.pending]: (state) => {
      state.fetchingSubscriptionCounts = true;
      state.errorFetchingSubscriptionCounts = false;
    },
    [getSubscriptionCounts.fulfilled]: (state, { payload }) => {
      state.fetchingSubscriptionCounts = false;
      state.errorFetchingSubscriptionCounts = false;
      

      const countMap = {};
      if (Array.isArray(payload.data?.subscriptionCounts)) {
        payload.data.subscriptionCounts.forEach((item) => {
          const apiId = item.apiId;
          const count = item.activeCount;
          if (!apiId) return;
          countMap[apiId] = count;


          const normalized = apiId.replace(/^api-(?:v\d+-)?/, '');
          if (normalized && normalized !== apiId) {
            countMap[normalized] = count;
          }
        });
      }
      state.subscriptionCounts = countMap;
    },
    [getSubscriptionCounts.rejected]: (state) => {
      state.fetchingSubscriptionCounts = false;
      state.errorFetchingSubscriptionCounts = true;
    },
    [deleteApi.fulfilled]: (state, action) => {
      state.deletingApi = false;
      state.apiDeleted = true;
      state.errorDeletingApi = false;
    },
    [deleteApi.pending]: (state) => {
      state.deletingApi = true;
      state.errorDeletingApi = false;
    },
    [deleteApi.rejected]: (state) => {
      state.deletingApi = false;
      state.errorDeletingApi = true;
    },
    [deleteBundle.fulfilled]: (state, action) => {
      state.deletingBundle = false;
      state.bundleDeleted = true;
      state.errorDeletingBundle = false;
      const { bundleId } = action.payload || {};
      removeBundleFromOrderState(state, bundleId);
    },
    [deleteBundle.pending]: (state) => {
      state.deletingBundle = true;
      state.errorDeletingBundle = false;
    },
    [deleteBundle.rejected]: (state) => {
      state.deletingBundle = false;
      state.errorDeletingBundle = true;
    },
    [getOrderAPIConsents.fulfilled]: (state, action) => {
      state.consents = action.payload;
      state.fetchingOrderAPIConsents = false;
      state.errorfetchingOrderAPIConsents = false;
    },
    [getOrderAPIConsents.pending]: (state) => {
      state.fetchingOrderAPIConsents = true;
      state.errorfetchingOrderAPIConsents = false;
    },
    [getOrderAPIConsents.rejected]: (state) => {
      state.fetchingOrderAPIConsents = false;
      state.errorfetchingOrderAPIConsents = true;
    },
    [generateNewSecret.pending]: (state) => {
      state.generatingSecret = true;
      state.errorInGeneratingSecret = false;
      state.fetchingOrderDetails = true;
    },
    [generateNewSecret.fulfilled]: (state, { payload }) => {
      state.generatingSecret = false;
      state.secretGenerated = true;
    },
    [generateNewSecret.rejected]: (state) => {
      state.generatingSecret = false;
      state.errorInGeneratingSecret = true;
    },
    [submitRestrictedAccessRequest.pending]: (state, action) => {
      const { apiId } = action.meta.arg;
      state.requestingRestrictedAccess = true;
      state.errorRequestingRestrictedAccess = false;
      state.requestingApiId = apiId;
    },

    [submitRestrictedAccessRequest.fulfilled]: (state, action) => {
      state.requestingRestrictedAccess = false;
      state.errorRequestingRestrictedAccess = false;
      state.restrictedAccessRequested = true;
      state.requestingApiId = null;
    },

    [submitRestrictedAccessRequest.rejected]: (state) => {
      state.requestingRestrictedAccess = false;
      state.errorRequestingRestrictedAccess = true;
      state.restrictedAccessRequested = false;
      state.requestingApiId = null;
    },
    [bundleSelectedLocations.fulfilled]: (state, action) => {
      state.bundleSelectedLocations = action.payload?.response.data;
      state.isBundleSelectedLocationsPending = false;
      state.isBundleSelectedLocationsErrored = false;
    },
    [bundleSelectedLocations.pending]: state => {
      state.isBundleSelectedLocationsPending = true;
      state.isBundleSelectedLocationsErrored = false;
    },
    [bundleSelectedLocations.rejected]: (state, action) => {
      state.isBundleSelectedLocationsPending = false;
      state.isBundleSelectedLocationsErrored = true;
      state.bundleSelectedLocationsError = action.error;
    },
    [addBundleLocations.pending]: state => {
      state.isAddBundleLocationsPending = true;
      state.isAddBundleLocationsSuccess = false;
      state.isAddBundleLocationsErrored = false;
      state.addBundleLocationsError = null;
    },
    [addBundleLocations.fulfilled]: (state, action) => { 
      state.isAddBundleLocationsPending = false;
      state.isAddBundleLocationsSuccess = true;
      state.isAddBundleLocationsErrored = false;
      state.addBundleLocationsResponse = action.payload;
    },
    [addBundleLocations.rejected]: (state, action) => {
      state.isAddBundleLocationsPending = false;
      state.isAddBundleLocationsSuccess = false;
      state.isAddBundleLocationsErrored = true;
      state.addBundleLocationsError = action.error;
    },
    [removeBundleLocations.pending]: (state) => {
      state.isRemoveBundleLocationsPending = true;
      state.isRemoveBundleLocationsSuccess = false;
      state.isRemoveBundleLocationsErrored = false;
      state.removeBundleLocationsError = null;
    },
    [removeBundleLocations.fulfilled]: (state, action) => {
      state.isRemoveBundleLocationsPending = false;
      state.isRemoveBundleLocationsSuccess = true;
      state.isRemoveBundleLocationsErrored = false;
      state.removeBundleLocationsResponse = action.payload;
    },
    [removeBundleLocations.rejected]: (state, action) => {
      state.isRemoveBundleLocationsPending = false;
      state.isRemoveBundleLocationsSuccess = false;
      state.isRemoveBundleLocationsErrored = true;
      state.removeBundleLocationsError = action.error;
    },
    [getStampedTermsDocuments.pending]: (state) => {
      state.stampedTermsDocumentsCallCount += 1;
      state.fetchingStampedTermsDocuments = true;
      state.errorFetchingStampedTermsDocuments = false;
    },
    [getStampedTermsDocuments.fulfilled]: (state, action) => {
      state.stampedTermsDocumentsCallCount = Math.max(0, state.stampedTermsDocumentsCallCount - 1);
      state.fetchingStampedTermsDocuments = state.stampedTermsDocumentsCallCount > 0;
      state.errorFetchingStampedTermsDocuments = false;
      
      // Accumulate documents from all bundles instead of replacing
      const incomingDocuments = action.payload || [];
      if (Array.isArray(incomingDocuments)) {
        incomingDocuments.forEach((incomingDoc) => {
          const existingIndex = state.stampedTermsDocuments.findIndex(
            (doc) => doc?.bundleName?.toLowerCase() === incomingDoc?.bundleName?.toLowerCase()
          );
          if (existingIndex >= 0) {
            state.stampedTermsDocuments[existingIndex] = incomingDoc;
          } else {
            state.stampedTermsDocuments.push(incomingDoc);
          }
        });
      }
    },
    [getStampedTermsDocuments.rejected]: (state) => {
      state.stampedTermsDocumentsCallCount = Math.max(0, state.stampedTermsDocumentsCallCount - 1);
      state.fetchingStampedTermsDocuments = state.stampedTermsDocumentsCallCount > 0;
      state.errorFetchingStampedTermsDocuments = true;
    },
    [saveBundleConsents.pending]: (state) => {
      state.savingBundleConsents = true;
      state.errorSavingBundleConsents = false;
    },
    [saveBundleConsents.fulfilled]: (state, action) => {
      state.savingBundleConsents = false;
      state.errorSavingBundleConsents = false;
      
      const responseItems = action.payload?.data.documents || [];
      const bundles = action.payload?.bundles || [];
      if (Array.isArray(responseItems)) {
        responseItems.forEach((item, index) => {
          const bundleId = bundles[index]?.bundleId || item?.bundleId || '';
          const consentLink = item?.location || '';
          const existingIndex = state.bundleConsentLinks.findIndex(
            (entry) => entry.bundleId?.toLowerCase() === bundleId.toLowerCase()
          );
          if (existingIndex >= 0) {
            state.bundleConsentLinks[existingIndex] = { bundleId, consentLink };
          } else if (bundleId) {
            state.bundleConsentLinks.push({ bundleId, consentLink });
          }
        });
      }
    },
    [saveBundleConsents.rejected]: (state) => {
      state.savingBundleConsents = false;
      state.errorSavingBundleConsents = true;
    },
  },
});

export const {
  updateSelectedFieldsMapping,
  updateWebhookURLMapping,
  updateSelectedPriceMapping,
  updateTermsAndConditionsMapping,
  updateCart,
  resetAddingPricingPlanToApiInCart,
  resetErrorCreatingOrder,
  resetErrorUpdatingOrder,
  clearToastNotification,
  resetState,
  setfromPurchaseFlowFlag,
  resetCreateandUpdateOrderFlags,
  storeDeletedApiName,
  storeDeletedBundleName,
  storeActiveApiList,
  clearAddBundleLocationsState,
  clearRemoveBundleLocationsState,
  updateBundleCMFSelectionInCart
} = orderAPISlice.actions;

export default orderAPISlice.reducer;
